import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { reclassifySelector } from './store/selector';
import { reclassifyApiService } from './services/api';
import {
    reclassifyInitAction,
    reclassifySuccessAction,
    reclassifyFailureAction,
    selectArticleAction,
    unselectArticleAction,
    setSelectedArticlesAction,
    clearSelectionAction,
    clearReclassifyAction,
    getArticlesInitAction,
    getArticlesSuccessAction,
    getArticlesFailureAction,
    setFiltersAction,
    getProductStatesInitAction,
    getProductStatesSuccessAction,
    getProductStatesFailureAction,
} from './store/reducer';
import { ReclassifyBulkRequest } from './interfaces/post-reclassify';
import { ReclassifyFilters } from './interfaces/get-articles-for-reclassify';
import { useContainerCore } from '../container';
import { useLocationCore } from '../location';
import { useCompanyCore } from '../company';
import { useBusinessCore } from '../business';

export function useReclassifyCore() {
    const reclassifyState = useSelector(reclassifySelector);
    const dispatch = useDispatch();

    // Usar hooks existentes
    const {
        containers,
        getContainers,
        clearContainer
    } = useContainerCore();

    const {
        locations,
        getLocations,
        clearLocation
    } = useLocationCore();

    const { companyId } = useCompanyCore();

    const { businesses } = useBusinessCore();

    // Reclasificar artículos
    const reclassifyArticles = useCallback(
        async (
            request: ReclassifyBulkRequest,
            onSuccess?: () => void,
            onFailure?: () => void
        ) => {
            try {
                dispatch(reclassifyInitAction());

                // Validar que tenemos companyId
                if (!companyId) {
                    throw new Error('No se encontró el ID de la empresa');
                }

                // Agregar companyId a la request
                const requestWithCompanyId = {
                    ...request,
                    companyId: companyId
                };

                const response = await reclassifyApiService.reclassifyArticles(requestWithCompanyId);
                dispatch(reclassifySuccessAction(response));
                onSuccess?.();
                return response;
            } catch (error: any) {
                console.error('Error reclassifying articles:', error);
                onFailure?.();
                dispatch(reclassifyFailureAction({ errorCode: error.request?.status || 500 }));
                throw error;
            }
        },
        [dispatch, companyId]
    );

    // Gestión de selección de artículos
    const selectArticle = useCallback(
        (articleId: number) => {
            dispatch(selectArticleAction(articleId));
        },
        [dispatch]
    );

    const unselectArticle = useCallback(
        (articleId: number) => {
            dispatch(unselectArticleAction(articleId));
        },
        [dispatch]
    );

    const selectAllArticles = useCallback(() => {
        if (reclassifyState.articles && reclassifyState.articles.length > 0) {
            const allIds = reclassifyState.articles.map(article => article.id);
            dispatch(setSelectedArticlesAction(allIds));
        }
    }, [reclassifyState.articles, dispatch]);

    const clearSelection = useCallback(() => {
        dispatch(clearSelectionAction());
    }, [dispatch]);

    // Obtener artículos seleccionados con sus datos completos
    const getSelectedArticlesData = useCallback(() => {
        if (!reclassifyState.articles || !reclassifyState.selectedArticles.length) {
            return [];
        }
        return reclassifyState.articles.filter(article =>
            reclassifyState.selectedArticles.includes(article.id)
        ).map(article => ({
            ...article,
            // Mapear campos para compatibilidad
            name: article.description,
            files: [],
            brand: '',
        }));
    }, [reclassifyState.articles, reclassifyState.selectedArticles]);

    // Verificar si un artículo está seleccionado
    const isArticleSelected = useCallback(
        (articleId: number) => {
            return reclassifyState.selectedArticles.includes(articleId);
        },
        [reclassifyState.selectedArticles]
    );

    // Limpiar estado completo
    const clearReclassify = useCallback(() => {
        dispatch(clearReclassifyAction());
        clearContainer();
        clearLocation();
    }, [dispatch, clearContainer, clearLocation]);

    // Cargar artículos usando el nuevo endpoint
    const loadArticles = useCallback(
        async (filters: ReclassifyFilters = {}) => {
            if (!companyId) return;

            try {
                dispatch(getArticlesInitAction());
                const response = await reclassifyApiService.getArticlesForReclassify(companyId, filters);
                dispatch(getArticlesSuccessAction(response));
            } catch (error: any) {
                console.error('Error loading articles:', error);
                dispatch(getArticlesFailureAction({ errorCode: error.request?.status || 500 }));
            }
        },
        [companyId, dispatch]
    );

    const loadContainers = useCallback(
        async (searchCode?: string, page?: number) => {
            if (!companyId) return;

            await getContainers(
                companyId,
                {
                    code: searchCode,
                    page: page || 1
                }
            );
        },
        [companyId, getContainers]
    );

    const loadLocations = useCallback(
        async () => {
            if (!companyId) return;

            await getLocations(companyId);
        },
        [companyId, getLocations]
    );

    const loadProductStates = useCallback(
        async () => {
            if (!companyId) return;

            try {
                dispatch(getProductStatesInitAction());
                const states = await reclassifyApiService.getProductStates(companyId);
                dispatch(getProductStatesSuccessAction(states));
            } catch (error: any) {
                console.error('Error loading product states:', error);
                dispatch(getProductStatesFailureAction({ errorCode: error.request?.status || 500 }));
            }
        },
        [companyId, dispatch]
    );

    // Gestión de filtros
    const setFilters = useCallback(
        (filters: ReclassifyFilters) => {
            dispatch(setFiltersAction(filters));
        },
        [dispatch]
    );

    // Obtener áreas de proveedores
    const getProviderAreas = useCallback(() => {
        const providers = businesses.filter((business) => business.is_provider);
        const allAreas = providers.reduce((areas, provider) => {
            if (provider.areas) {
                return [
                    ...areas,
                    ...provider.areas.map(area => ({
                        ...area,
                        provider_name: provider.name,
                        provider_id: provider.id
                    }))
                ];
            }
            return areas;
        }, [] as any[]);
        return allAreas;
    }, [businesses]);

    return {
        // Estado
        selectedArticles: reclassifyState.selectedArticles,
        reclassifyLoading: reclassifyState.reclassifyLoading,
        error: reclassifyState.error,
        lastReclassifyResult: reclassifyState.lastReclassifyResult,

        // Datos de artículos
        articles: reclassifyState.articles,
        articlesLoading: reclassifyState.articlesLoading,
        pagination: reclassifyState.pagination,
        filters: reclassifyState.filters,

        // Datos de otros módulos
        containers: containers || [],
        locations: locations || [],
        productStates: reclassifyState.productStates,
        productStatesLoading: reclassifyState.productStatesLoading,

        // Métodos principales
        reclassifyArticles,
        loadArticles,
        loadContainers,
        loadLocations,
        loadProductStates,

        // Gestión de filtros
        setFilters,
        getProviderAreas,

        // Gestión de selección
        selectArticle,
        unselectArticle,
        selectAllArticles,
        clearSelection,
        getSelectedArticlesData,
        isArticleSelected,

        // Utilidades
        clearReclassify,

        // Recodificar
        recodeProduct: async (companyId: number, productStorageId: number, request: any) => {
            try {
                const response = await reclassifyApiService.recodeProduct(companyId, productStorageId, request);
                return response;
            } catch (error) {
                console.error('Error recoding product:', error);
                throw error;
            }
        },

        recodeProductV2: async (companyId: number, productStorageId: number, request: any) => {
            try {
                const response = await reclassifyApiService.recodeProductV2(companyId, productStorageId, request);
                return response;
            } catch (error) {
                console.error('Error recoding product V2:', error);
                throw error;
            }
        },
    };
} 